import React, { useEffect, useState, useCallback, useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Container,
  Row,
  Card,
  CardHeader,
  Button,
  Col,
  FormGroup,
  Label,
  CardBody,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';

import SimpleHeader from '../../../../components/Headers/SimpleHeader.js';
import Pagination from '../../../../components/Pagination';
import ReactLoader from '../../../../components/Loader';
import ToggleSwitch from '../../../../components/ToggleSwitch/ToggleSwitch';
import useDebounce from '../../../ServicePanel/components/QueueContent/components/Contact/ContactActiveModal/hooks/useDebaunce';
import {
  icon_column_style,
  individual_column_style,
  individual_icon_header_style,
  individual_header_style
} from '../../../../assets/styles/table_custom_style';

import MatrixSLAHolidays from '../components/matrixSLAHolidays';
import ServiceClassification from '../components/serviceClassifications';
import { DEFAULT_PAGE_SIZE } from '../../utils';

import MatricesSLAService from '../service';

const MatrixSLAList = () => {
  const [matricesSLA, setMatricesSLA] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [quantity, setQuantity] = useState(0);
  const [showDisabled, setShowDisable] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [idResource, setIdResource] = useState(null);
  const [isModalHolidaysOpen, setIsModalHolidaysOpen] = useState(false);
  const [
    isModalServiceClassificationOpen,
    setIsModalServiceClassificationOpen
  ] = useState(false);
  const debouncedSearchValue = useDebounce(searchValue, 1000);

  const getMatricesSLA = useCallback(
    (selectedPage = 1, q = '') => {
      const params = {
        page: selectedPage || activePage,
        size: DEFAULT_PAGE_SIZE,
        showDisabled,
        q
      };

      MatricesSLAService.getMatrixSLA(params)
        .then((res) => {
          const matricesSLA = res.data.data.content;
          const quantity = res.data.data.quantity;
          setMatricesSLA(matricesSLA);
          setQuantity(quantity);

          if (quantity === 0) {
            toast.info('Nenhuma Matriz SLA encontrada', {
              autoClose: 3000,
              closeOnClick: true
            });
          }

          setLoading(false);
        })
        .catch(() => {
          toast.error('Erro ao listar as Matrizes SLA', {
            autoClose: 3000,
            closeOnClick: true
          });
        });
    },
    [activePage, showDisabled]
  );

  useEffect(() => {
    getMatricesSLA(activePage, debouncedSearchValue);
  }, [activePage, debouncedSearchValue, getMatricesSLA]);

  const handlePageChange = useCallback(
    (activePage) => {
      setActivePage(activePage);
      getMatricesSLA(activePage, debouncedSearchValue);
    },
    [debouncedSearchValue, getMatricesSLA]
  );

  const goToPage = useCallback(
    (newPage) => {
      handlePageChange(newPage);
    },
    [handlePageChange]
  );

  const handleEnableDisabledCustomerPortfolio = useCallback(
    async (id, data, usrLogin) => {
      await MatricesSLAService.enableDisableMatrixSLA(id, data, usrLogin)
        .then(() => {
          toast.success(
            `Matriz SLA ${
              data?.enabled ? 'habilitada' : 'desabilitada'
            } com sucesso`,
            {
              autoClose: 3000,
              closeOnClick: true
            }
          );
        })
        .catch((error) => {
          const msg = error?.response?.data?.message;
          toast.error(
            msg ||
              `Erro ao ${
                data?.enabled ? 'habilitar' : 'desabilitar'
              } Matriz SLA`,
            {
              autoClose: 3000,
              closeOnClick: true
            }
          );
        });
    },
    []
  );

  const toggleMatrixSLAStatus = useCallback(
    ({ id, isActive }) => {
      const usrLogin = localStorage.getItem('login');
      const data = { enabled: !isActive };
      try {
        handleEnableDisabledCustomerPortfolio(id, data, usrLogin);
      } catch (error) {
        const msg = error?.response?.data?.message;
        toast.error(
          msg ||
            `Erro ao ${
              isActive ? 'desabilitar' : 'habilitar'
            } a Matriz SLA selecionada`
        );
      }
      setTimeout(() => {
        goToPage(1);
      }, 300);
    },
    [handleEnableDisabledCustomerPortfolio, goToPage]
  );

  const columns = useMemo(
    () => [
      {
        dataField: 'name',
        text: 'Nome',
        sort: true,
        style: individual_column_style,
        headerStyle: individual_header_style
      },
      {
        dataField: 'customer_portfolio.name',
        text: 'Categoria de Cliente',
        sort: true,
        style: individual_column_style,
        headerStyle: individual_header_style
      },
      {
        dataField: 'edit',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        text: 'Editar',
        formatter: (_, row) => {
          return (
            <Link to={'/admin/matrix-sla/form/' + row.id}>
              <Button color="info" size="sm">
                <li className="fas fa-user-edit"></li>
              </Button>
            </Link>
          );
        }
      },
      {
        dataField: 'service_classification',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        text: 'Classificação de Serviço',
        formatter: (_, row) => {
          return (
            <>
              <Button
                color="info"
                size="sm"
                title="Classificação de Serviço"
                onClick={() => {
                  setIdResource(row.id);
                  setIsModalServiceClassificationOpen(true);
                }}
              >
                <li className="fas fa-table"></li>
              </Button>
            </>
          );
        }
      },
      {
        dataField: 'holidays',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        text: 'Feriados',
        formatter: (_, row) => {
          return (
            <>
              <Button
                color="info"
                size="sm"
                title="Feriados"
                onClick={() => {
                  setIdResource(row.id);
                  setIsModalHolidaysOpen(true);
                }}
              >
                <li className="fas fa-calendar"></li>
              </Button>
            </>
          );
        }
      },
      {
        dataField: 'habilitar/desabilitar',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        text: 'Habilitar/Desabilitar',
        formatter: (_, row) => {
          return (
            <ToggleSwitch
              checked={row.enabled}
              id={row.id}
              onChange={() =>
                toggleMatrixSLAStatus({
                  id: row.id,
                  isActive: row.enabled
                })
              }
            />
          );
        }
      }
    ],
    [toggleMatrixSLAStatus]
  );

  const handleShowDisableds = (value) => {
    setShowDisable(value);
    if (value === false) setActivePage(1);
  };

  return (
    <>
      <SimpleHeader
        setCreate="/admin/matrix-sla/form"
        name="Form validation"
        parentName="Forms"
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Matrizes SLA</h3>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="8">
                      <InputGroup>
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="fas fa-search" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Pesquisar"
                          type="text"
                          onChange={(event) =>
                            setSearchValue(event.target.value)
                          }
                        />
                      </InputGroup>
                    </Col>
                    <Col md="4" className="text-right mt-2">
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            checked={showDisabled}
                            onChange={() => handleShowDisableds(!showDisabled)}
                          />
                          <span>Mostrar Matrizes desabilitadas</span>
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
                <ToolkitProvider
                  data={matricesSLA}
                  keyField="id"
                  columns={columns}
                >
                  {(props) => (
                    <div className="py-4 table-responsive">
                      {loading ? (
                        <Row className="justify-content-md-center">
                          <ReactLoader />
                        </Row>
                      ) : (
                        <BootstrapTable
                          {...props.baseProps}
                          bootstrap4={true}
                          bordered={false}
                          classes="align-items-center"
                        />
                      )}
                    </div>
                  )}
                </ToolkitProvider>
                <Pagination
                  activePage={activePage}
                  totalItemsCount={quantity}
                  onChange={handlePageChange.bind(this)}
                />
              </Card>
            </div>
          </div>
        </Row>
      </Container>
      <MatrixSLAHolidays
        isModalOpen={isModalHolidaysOpen}
        onModalToggle={setIsModalHolidaysOpen}
        matrixSLAId={idResource}
        unmountOnClose={true}
      />
      <ServiceClassification
        isModalOpen={isModalServiceClassificationOpen}
        onModalToggle={setIsModalServiceClassificationOpen}
        matrixSLAId={idResource}
        unmountOnClose={true}
      />
    </>
  );
};

export default MatrixSLAList;
